import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Error as ChicagoError } from '@fnModels/chicago';
import { ChicagoFunctions } from '@functions/chicago.service';
import { MatterBusinessUser, MatterFileType } from '@models/matter';
import { SubmitData } from '@models/title-insurance/chicago';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { ChicagoSelectors } from '@state/global/title-insurance/chicago';
import { SelectedMatter } from '@state/matter/matter.selectors';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import { ICON } from '@core/util/icon';
import { envTypes } from 'src/environments/env.types';
import { environment } from 'src/environments/environment';
import { UpdateChicagoService } from '../services/update-chicago.service';
import { firstValueFrom } from 'rxjs';

export interface ChicagoPortalData {
	submitNewOrder: boolean;
}
@UntilDestroy()
@Component({
	selector: 'app-chicago-portal',
	templateUrl: './chicago-portal.component.html',
	styleUrls: ['./chicago-portal.component.scss'],
	standalone: false,
})
export class ChicagoPortalComponent implements OnInit {
	public loading = true;
	public buttonLoading = false;
	public iframeUrl: SafeResourceUrl;
	public ICON = ICON;
	private thirdPartyId: number;
	public submitNewOrder: boolean;
	public errors: string[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.assignedLawyer) lawyer: MatterBusinessUser;
	@SelectSnapshot(SelectedMatter.assignedClerk) clerk: MatterBusinessUser;
	@SelectSnapshot(SelectedMatter.titleInsuranceThirdPartyId)
	thirdPartyTitleInsuranceId: number;
	@SelectSnapshot(ChicagoSelectors.getChicagoDataPayload) payload: SubmitData;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(SelectedMatter.matterNumber) matterNumber: string;
	@SelectSnapshot(SelectedMatter.matterName) matterName: string;

	constructor(
		private chicagoFunction: ChicagoFunctions,
		private dialogRef: MatDialogRef<ChicagoPortalComponent>,
		private sanitizer: DomSanitizer,
		private updateChicagoService: UpdateChicagoService,
		@Inject(MAT_DIALOG_DATA) public _data: ChicagoPortalData,
	) {}

	async ngOnInit(): Promise<void> {
		this.dialogRef.disableClose = true;
		this.dialogRef.addPanelClass(['vw-90', 'vh-90']);

		this.dialogRef
			.backdropClick()
			.pipe(untilDestroyed(this))
			.subscribe(async () => {
				await this.beforeClose();
			});

		this.submitNewOrder = this._data.submitNewOrder;
		await this.upload();
	}

	async retry() {
		await this.upload();
	}

	async upload() {
		this.loading = true;
		this.errors = null;

		LogRocket.debug('Payload sent to Chicago', this.payload);
		if (environment.env !== envTypes.PRODUCTION) {
			console.log('Payload sent to Chicago', this.payload);
		}

		try {
			const thirdPartyTitleInsuranceId = this.submitNewOrder
				? 0
				: this.thirdPartyTitleInsuranceId;
			const result = await this.chicagoFunction
				.submitData(
					this.lawyer.id,
					this.payload,
					this.matterId,
					thirdPartyTitleInsuranceId,
					this.clerk?.id,
				)
				.pipe(take(1))
				.toPromise();

			LogRocket.debug('Chicago response', result);

			this.thirdPartyId =
				result.FileNumber > 0 ? result.FileNumber : thirdPartyTitleInsuranceId;

			//FileNumber returns as 0 when resubmitting
			if (result.FileNumber > 0) {
				//This is to remove undefined values from the payload to keep firebase happy
				const prevPayload = JSON.parse(JSON.stringify(this.payload));
				await this.updateChicagoService.updateChicagoData(
					result.FileNumber,
					prevPayload,
				);
			}

			this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
				result.PortalUrl,
			);
		} catch (err) {
			if (err.details?.errors?.length > 0) {
				LogRocket.error('Chicago - submit data error: ', err.details);
				this.errors = err.details.errors.map(
					(x) => (x as ChicagoError).ErrorMessage,
				);

				return;
			}

			this.errors = [
				'If the issue continues please reach out to our support team for additional help.',
			];
			LogRocket.error('Chicago - submit data error:', err);
		} finally {
			this.loading = false;
		}
	}

	async beforeClose() {
		const { thirdPartyId, matterId } = this;
		if (thirdPartyId && matterId) {
			this.buttonLoading = true;
			await firstValueFrom(
				this.chicagoFunction.getAllStatusDetails(thirdPartyId, matterId),
			);
		}
		this.buttonLoading = false;
		this.dialogRef.close();
	}
}
